import { useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import Button from "app/storybookComponents/Button";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import { getEntries, getS } from "utils/helperFunctions";
import { ChangesSummary } from "./types";
import ConfirmationBox from "./ConfirmationBox";
import {
  deleteUserByUserAccountIdWithExtraSteps,
  selectUpdateOrganizationalStructureResponse,
} from "./slice";
import { constructPayloadFromChangesSummary } from "./helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/pro-regular-svg-icons";

interface Props {
  changesSummary: ChangesSummary;
  deactivatedUserAccountId?: number | null;
  onStepBack: () => void;
  onStepForward: () => void;
  directReportsUserAccountIds?: number[];
  navigateToPastStep?: (
    step: "direct-reports" | "team-changes" | "department-changes"
  ) => void;
}

const ReviewChangesMadeStep = ({
  changesSummary,
  deactivatedUserAccountId,
  onStepBack,
  onStepForward,
  directReportsUserAccountIds,
  navigateToPastStep,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const departmentsById = useAppSelector(selectDepartments);
  const updateOrganizationalStructureResponse = useAppSelector(
    selectUpdateOrganizationalStructureResponse
  );
  const [loading, setLoading] = useState(false);

  const getUserFullName = (
    userAccountId?: number | null,
    fallBack: "User" | "emailAddress" = "User"
  ) => {
    const user = userAccountId ? usersById[userAccountId] : null;
    return (
      `${user?.firstName} ${user?.lastName}`.trim() ||
      (fallBack === "User" ? "User" : user?.emailAddress)
    );
  };

  const getTotalTeamsDeletedAndReassigned = () => {
    let totalTeamsDeleted = 0;
    let totalTeamsReassigned = 0;

    if (changesSummary?.teamChanges) {
      Object.values(changesSummary?.teamChanges).forEach((changes) => {
        if (changes.deleteTeam) {
          totalTeamsDeleted++;
        }
        if (changes.newTeamLeaderUserAccountId) {
          totalTeamsReassigned++;
        }
      });
    }
    return { totalTeamsDeleted, totalTeamsReassigned };
  };

  const getSummaryBox = () => {
    const userName = getUserFullName(deactivatedUserAccountId);
    const { totalTeamsDeleted, totalTeamsReassigned } =
      getTotalTeamsDeletedAndReassigned();

    return (
      <div className="bg-blue-50 p-4 rounded-lg color-blue-800 border border-blue-200">
        <p>
          <b>Summary</b>
        </p>
        <ul className="list-disc pl-[16px]">
          <li>
            <p>{userName} will be deactivated</p>
          </li>
          {changesSummary?.automaticTeamChanges?.newManagerUserAccountId ? (
            <li>
              <p>
                {directReportsUserAccountIds?.length ?? 0} direct report
                {getS(directReportsUserAccountIds?.length ?? 0)} will be
                reassigned
              </p>
            </li>
          ) : null}
          {changesSummary?.automaticTeamChanges?.unassignManagerForAllUsers ? (
            <li>
              <p>
                {directReportsUserAccountIds?.length ?? 0} direct report
                {getS(directReportsUserAccountIds?.length ?? 0)} will be
                unassigned
              </p>
            </li>
          ) : null}
          {totalTeamsDeleted ? (
            <li>
              <p>
                {totalTeamsDeleted} team
                {getS(totalTeamsDeleted)} will be deleted
              </p>
            </li>
          ) : null}
          {totalTeamsReassigned ? (
            <li>
              <p>
                {totalTeamsReassigned} team
                {getS(totalTeamsReassigned)} will be have their leader changed
              </p>
            </li>
          ) : null}
          {changesSummary?.departmentChanges ? (
            <li>
              <p>
                {Object.keys(changesSummary?.departmentChanges).length}{" "}
                department
                {getS(
                  Object.keys(changesSummary?.departmentChanges).length
                )}{" "}
                will be handled
              </p>
            </li>
          ) : null}
          {changesSummary?.automaticTeamChanges?.newManagerUserAccountId ||
          changesSummary?.automaticTeamChanges?.unassignManagerForAllUsers ? (
            <li>
              <p>
                Automatic team "{getUserFullName(deactivatedUserAccountId)}'s
                Team" will be deleted
              </p>
            </li>
          ) : null}
          {changesSummary?.automaticTeamChanges?.newManagerUserAccountId ? (
            <li>
              <p>
                New Automatic team "
                {getUserFullName(
                  changesSummary?.automaticTeamChanges?.newManagerUserAccountId
                )}
                's Team" will be created
              </p>
            </li>
          ) : null}
        </ul>
      </div>
    );
  };

  const getRightContent = (
    text: string,
    isDangerText: boolean,
    isAPIResponseSuccessful?: boolean | null,
    onAPIResponseClick?: () => void
  ) => {
    return (
      <div className="flex items-center gap-2">
        {typeof isAPIResponseSuccessful === "boolean" ? (
          <FontAwesomeIcon
            icon={isAPIResponseSuccessful ? faCircleCheck : faCircleXmark}
            onClick={onAPIResponseClick}
            className={
              isAPIResponseSuccessful ? "text-green-500" : "text-red-500"
            }
          />
        ) : null}
        <p className={isDangerText ? "text-red-500" : ""}>{text}</p>
      </div>
    );
  };

  const getTeamChangesConfirmationBox = () => {
    if (!changesSummary.teamChanges) {
      return null;
    }
    const rows = getEntries(changesSummary.teamChanges).map(
      ([teamId, changes]) => {
        const team = teamsById[Number(teamId)];
        const newTeamLeaderName = getUserFullName(
          changes.newTeamLeaderUserAccountId,
          "emailAddress"
        );

        return {
          leftContent: <p>{team?.teamName}</p>,
          rightContent: getRightContent(
            changes.deleteTeam
              ? "Team will be deleted"
              : `Leader will be changed to ${newTeamLeaderName}`,
            !!changes.deleteTeam,
            updateOrganizationalStructureResponse?.teamChanges?.[teamId]
              ?.isValid,
            () => {
              navigateToPastStep?.("team-changes");
            }
          ),
        };
      }
    );
    return (
      <ConfirmationBox
        title="Teams"
        rows={rows}
        key="team-changes"
        expandedEntityName="team"
      />
    );
  };

  const getDepartmentChangesConfirmationBox = () => {
    if (!changesSummary.departmentChanges) {
      return null;
    }
    const rows = getEntries(changesSummary.departmentChanges).map(
      ([departmentId, changes]) => {
        const department = departmentsById[Number(departmentId)];
        const newDeptLeaderName = getUserFullName(
          changes.newDepartmentLeaderUserAccountId,
          "emailAddress"
        );

        return {
          leftContent: <p>{department?.name ?? ""}</p>,
          rightContent: getRightContent(
            changes.removeDepartmentLeader
              ? "Department leader will be removed"
              : `Leader will be changed to ${newDeptLeaderName}`,
            !!changes.removeDepartmentLeader,
            updateOrganizationalStructureResponse?.departmentChanges?.[
              departmentId
            ]?.isValid,
            () => {
              navigateToPastStep?.("department-changes");
            }
          ),
        };
      }
    );

    return (
      <ConfirmationBox
        title="Departments"
        rows={rows}
        expandedEntityName="department"
        key="department-changes"
      />
    );
  };

  const getDirectReportChangesConfirmationBox = () => {
    if (!changesSummary.automaticTeamChanges) {
      return null;
    }
    const rows =
      directReportsUserAccountIds?.map((userAccountId) => {
        const userName = getUserFullName(userAccountId);
        const newManagerName = getUserFullName(
          changesSummary.automaticTeamChanges?.newManagerUserAccountId,
          "emailAddress"
        );
        return {
          leftContent: <p>{userName}</p>,
          rightContent: changesSummary.automaticTeamChanges
            ?.unassignManagerForAllUsers ? (
            <p className="text-red-500">Manager will be unassigned</p>
          ) : (
            <p>Manager will be changed to {newManagerName}</p>
          ),
        };
      }) || [];

    return (
      <ConfirmationBox
        title="Automatic Teams"
        rows={rows}
        expandedEntityName="automatic-team"
        key="automatic-team-changes"
      />
    );
  };

  const getChangesSummary = () => {
    const summarySections: JSX.Element[] = [];

    const directReportChangesBox = getDirectReportChangesConfirmationBox();
    if (directReportChangesBox) {
      summarySections.push(directReportChangesBox);
    }

    const teamChangesBox = getTeamChangesConfirmationBox();
    if (teamChangesBox) {
      summarySections.push(teamChangesBox);
    }

    const departmentChangesBox = getDepartmentChangesConfirmationBox();
    if (departmentChangesBox) {
      summarySections.push(departmentChangesBox);
    }

    return summarySections;
  };

  const onModalSubmit = async () => {
    if (!deactivatedUserAccountId) {
      return;
    }

    setLoading(true);
    try {
      const payload = constructPayloadFromChangesSummary(
        changesSummary,
        directReportsUserAccountIds ?? []
      );

      await dispatch(
        deleteUserByUserAccountIdWithExtraSteps({
          payload,
          userToDeactivate: deactivatedUserAccountId,
        })
      ).unwrap();

      setLoading(false);
      onStepForward();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      {getSummaryBox()}
      {getChangesSummary()}
      <div className="flex justify-end gap-2">
        <Button onClick={onStepBack} variant="secondary-gray">
          Back
        </Button>
        <Button onClick={onModalSubmit} disabled={loading}>
          Submit
        </Button>
      </div>
    </>
  );
};

export default ReviewChangesMadeStep;
